/**
 * The learner's reads of this book, as a progression.
 *
 * The report answers "how did I do"; a learner who has finished a book twice has a
 * second question — *better than last time?* — and a count of reads cannot answer
 * it. So this section prints the reads in order, draws them, and names the change
 * between them. It is the only part of the report that is about the learner's
 * history rather than about one sitting, and it appears only when there is a
 * history to speak of: one read is the report the learner is already reading.
 *
 * Three decisions shape it.
 *
 *  - **The chart is proportional, not normalised.** Bars are drawn against the
 *    full hundred points, not against the range this learner happens to span, so a
 *    42% and an 71% look like a 42% and an 71% — a chart scaled to its own smallest
 *    and largest value would draw a wobble between 68% and 71% as the same climb as
 *    20% to 71%, which is the opposite of the truth it is there to tell.
 *  - **Nothing is drawn that did not happen.** A scoreless read (a book with
 *    nothing to score, or one whose exercises came later) has no bar and no
 *    percentage, and a read that stopped part-way says so rather than being shown
 *    as a low score — see `#/lib/flashbookReadHistory`, which decides all of this
 *    and is unit-tested on its own.
 *  - **Newest first in the list, oldest first in the chart.** A list is read the
 *    way the learner remembers the reads — the one they just finished at the top —
 *    while a progression is read left to right. The two orders are both correct
 *    and they are different, so they are stated rather than left to look like a bug.
 */
import {useState} from 'react'
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {
  type FlashBookReadHistory,
  type ReadHistoryRow,
} from '#/lib/flashbookReadHistory'
import {useFlashBookPlayPalette} from '#/components/flashbooks/FlashBookPlayTheme'
import {
  useCopyStyle,
  useReportType,
} from '#/components/flashbooks/useReportChrome'

/** How many reads the list shows before it offers the rest. */
const ROWS_SHOWN = 4

/** The height a bar with no percentage at all would have, so a 0% is still seen. */
const BAR_FLOOR = 4

/** The height a bar for a perfect read gets, in points. */
const BAR_CEILING = 44

type Props = {
  history: FlashBookReadHistory
}

export function FlashBookReadHistorySection({history}: Props) {
  const {t: l} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()
  const [expanded, setExpanded] = useState(false)

  // Newest first, because the read the learner just finished is the one they are
  // looking for (see the module note on the two orders).
  const rows = [...history.reads].reverse()
  const shown = expanded ? rows : rows.slice(0, ROWS_SHOWN)
  const hidden = rows.length - shown.length

  /*
   * The one sentence above the list: whether they are getting better.
   *
   * It compares first with last rather than last with previous, because that is
   * the claim a learner tests by scrolling — and it says nothing about movement
   * until two reads have been scored, because "up 0" is not news. Written here
   * rather than in a helper so the wording is translated where it is used, like
   * every other string on this screen.
   */
  const gain = history.improvement
  const lead =
    history.scored < 2 || gain === null
      ? l`One scored read so far — read it again to see how you have moved.`
      : gain > 0
        ? history.improvingRun >= 2
          ? l`Up ${history.scored} points since your first read, and better for the last ${history.improvingRun} reads.`
          : l`Up ${gain} points since your first read.`
        : gain < 0
          ? l`Down ${Math.abs(gain)} points since your first read.`
          : l`Holding steady across ${history.scored} scored reads.`

  return (
    <View testID="flashbook-report-history" style={styles.section}>
      <Text style={[type.title, copy.text, {color: pal.text}, styles.title]}>
        <Trans>Your reads</Trans>
      </Text>

      <Text
        testID="flashbook-report-history-lead"
        style={[type.body, copy.text, {color: pal.body}]}>
        {lead}
      </Text>

      {history.series.length > 1 && (
        <View
          testID="flashbook-report-history-chart"
          style={[
            styles.chart,
            {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
          ]}>
          <View testID="flashbook-report-history-bars" style={styles.bars}>
            {history.series.map((pct, index) => (
              <View
                key={`${index}-${pct}`}
                style={[
                  styles.bar,
                  {
                    height: barHeight(pct),
                    // The best read is the one the bars are climbing towards, so it
                    // is the only one at full strength.
                    opacity: history.bestPct === pct ? 1 : 0.42,
                    backgroundColor: pal.accent,
                  },
                ]}
              />
            ))}
          </View>
          <View style={styles.chartFoot}>
            <Text style={[type.caption, copy.text, {color: pal.muted}]}>
              {l`First ${history.firstPct ?? 0}%`}
            </Text>
            <Text style={[type.caption, copy.text, {color: pal.muted}]}>
              {l`Now ${history.lastPct ?? 0}%`}
            </Text>
          </View>
        </View>
      )}

      {shown.map(row => (
        <ReadRow key={row.id} row={row} accent={pal.accent} best={row.best} />
      ))}

      {hidden > 0 && (
        <TouchableOpacity
          testID="flashbook-report-history-more"
          accessibilityRole="button"
          onPress={() => setExpanded(true)}
          style={styles.moreBtn}>
          <Text style={[type.caption, copy.text, {color: pal.accent}]}>
            <Plural
              value={hidden}
              one="Show one earlier read"
              other="Show all # earlier reads"
            />
          </Text>
        </TouchableOpacity>
      )}

      {history.practices > 0 && (
        <Text
          testID="flashbook-report-history-practices"
          style={[type.caption, copy.text, {color: pal.muted}]}>
          <Plural
            value={history.practices}
            one="Plus one practice of the questions worth another look."
            other="Plus # practices of the questions worth another look."
          />
        </Text>
      )}

      {/* The register is bounded (see `#/lib/flashbookProgress`), and a learner who
          has read a book a great many times should be told that the list has a
          floor rather than left to wonder where their earlier reads went. */}
      {history.totalReads > history.reads.length && (
        <Text
          testID="flashbook-report-history-trimmed"
          style={[type.caption, copy.text, {color: pal.muted}]}>
          {l`This device keeps the last ${history.reads.length} reads.`}
        </Text>
      )}
    </View>
  )
}

/** A bar's height in points: proportional to the hundred, never invisible. */
function barHeight(pct: number): number {
  const exact =
    BAR_FLOOR +
    (Math.max(0, Math.min(100, pct)) / 100) * (BAR_CEILING - BAR_FLOOR)
  return Math.round(exact)
}

/** One read, as a line: which read, when, how it went, and what changed. */
function ReadRow({
  row,
  accent,
  best,
}: {
  row: ReadHistoryRow
  accent: string
  best: boolean
}) {
  const {t: l, i18n} = useLingui()
  const type = useReportType()
  const copy = useCopyStyle()
  const pal = useFlashBookPlayPalette()

  /*
   * How far the read got, said plainly. A read that stopped part-way is not a low
   * score, it is an unfinished read, and the page it stopped on is the useful
   * fact — the learner may want to go back to it.
   */
  const where = row.completed
    ? l`finished`
    : l`stopped on page ${row.reachedPage}`

  const past = i18n.date(new Date(row.endedAt), {dateStyle: 'medium'})

  return (
    <View
      testID={`flashbook-report-history-read--${row.number}`}
      style={[
        styles.row,
        {borderColor: pal.paperHairline, backgroundColor: pal.surfaceAlt},
        copy.row,
      ]}>
      <View style={styles.rowTop}>
        <Text style={[type.label, copy.text, {color: pal.text}]}>
          {l`Read ${row.number}`}
        </Text>
        {best && (
          <View
            testID={`flashbook-report-history-best--${row.number}`}
            style={[styles.bestChip, {borderColor: accent}]}>
            <Text style={[type.caption, copy.text, {color: accent}]}>
              <Trans>best</Trans>
            </Text>
          </View>
        )}
        <View style={styles.grow} />
        <Text
          testID={`flashbook-report-history-pct--${row.number}`}
          style={[type.title, copy.text, {color: best ? accent : pal.text}]}>
          {row.pct === null ? l`no score` : `${row.pct}%`}
        </Text>
      </View>

      <View style={styles.rowFoot}>
        <Text style={[type.caption, copy.text, {color: pal.muted}]}>
          {past}
          {' · '}
          {where}
        </Text>
        <View style={styles.grow} />
        {row.delta !== null && row.delta !== 0 && (
          <View style={styles.delta}>
            <Feather
              name={row.delta > 0 ? 'trending-up' : 'trending-down'}
              size={12}
              color={row.delta > 0 ? pal.positive : pal.negative}
            />
            <Text
              testID={`flashbook-report-history-delta--${row.number}`}
              style={[
                type.caption,
                copy.text,
                {color: row.delta > 0 ? pal.positive : pal.negative},
              ]}>
              {row.delta > 0 ? `+${row.delta}` : `${row.delta}`}
            </Text>
          </View>
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  section: {gap: 8},
  title: {marginBottom: 2},
  chart: {borderWidth: 1, borderRadius: 12, padding: 12, gap: 8},
  bars: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 4,
    height: BAR_CEILING,
  },
  bar: {flex: 1, borderRadius: 3},
  chartFoot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  row: {borderWidth: 1, borderRadius: 12, padding: 11, gap: 4},
  rowTop: {flexDirection: 'row', alignItems: 'center', gap: 7},
  rowFoot: {flexDirection: 'row', alignItems: 'center', gap: 6},
  grow: {flex: 1},
  bestChip: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 7,
    paddingVertical: 1,
  },
  delta: {flexDirection: 'row', alignItems: 'center', gap: 3},
  moreBtn: {paddingVertical: 3},
})
